import {
  FieldErrors,
  FieldType,
  FormDefinition,
  FormField,
  FormStep,
} from './cardTypes';

const REQUIRED = 'Required';
const FORMAT = 'Use the format shown';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_SEPARATORS = /[\s()+-]/g;
const PHONE_DIGITS = /^\d{7,15}$/;
const WHOLE_NUMBER_TYPES: FieldType[] = ['integer', 'durationMinutes'];
const NUMBER_TYPES: FieldType[] = ['number', ...WHOLE_NUMBER_TYPES];

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasOwn = (container: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(container, key);

const ownValue = (container: unknown, key: string): unknown =>
  isRecord(container) && hasOwn(container, key) ? container[key] : undefined;

const deepEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((item, index) => deepEqual(item, right[index]))
    );
  }
  if (isRecord(left) && isRecord(right)) {
    const keys = Object.keys(left);
    return (
      keys.length === Object.keys(right).length &&
      keys.every(key => hasOwn(right, key) && deepEqual(left[key], right[key]))
    );
  }
  return false;
};

const assignPath = (
  container: Record<string, unknown>,
  segments: string[],
  value: unknown,
): Record<string, unknown> => {
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    return { ...container, [head]: value };
  }
  const child = ownValue(container, head);
  const next = assignPath(isRecord(child) ? child : {}, rest, value);
  return { ...container, [head]: next };
};

const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (typeof value === 'number') {
    return Number.isNaN(value);
  }
  return Array.isArray(value) && value.length === 0;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return Number(value);
  }
  return Number.NaN;
};

const checkNumber = (
  field: FormField,
  value: unknown,
  whole: boolean,
): string | undefined => {
  const number = toNumber(value);
  if (!Number.isFinite(number) || (whole && !Number.isInteger(number))) {
    return FORMAT;
  }
  if (field.min != null && number < field.min) {
    return `Must be at least ${field.min}`;
  }
  if (field.max != null && number > field.max) {
    return `Must be at most ${field.max}`;
  }
  return undefined;
};

const checkType = (field: FormField, value: unknown): string | undefined => {
  if (NUMBER_TYPES.includes(field.type)) {
    return checkNumber(field, value, WHOLE_NUMBER_TYPES.includes(field.type));
  }
  if (field.type === 'numberList') {
    return Array.isArray(value)
      ? value
          .map(item => checkNumber(field, item, false))
          .find(message => message !== undefined)
      : FORMAT;
  }
  if (field.type === 'email') {
    return typeof value === 'string' && EMAIL.test(value)
      ? undefined
      : 'Enter a valid email address';
  }
  if (field.type === 'phone') {
    const digits =
      typeof value === 'string' ? value.replace(PHONE_SEPARATORS, '') : '';
    return PHONE_DIGITS.test(digits) ? undefined : 'Enter a valid phone number';
  }
  return undefined;
};

const matchesPattern = (pattern: string, value: string): boolean => {
  try {
    return new RegExp(`^(?:${pattern})$`).test(value);
  } catch {
    return true;
  }
};

const checkText = (field: FormField, value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  if (field.maxLength != null && value.length > field.maxLength) {
    return `Use ${field.maxLength} characters or fewer`;
  }
  if (field.pattern != null && !matchesPattern(field.pattern, value)) {
    return FORMAT;
  }
  return undefined;
};

export const getPath = (
  values: Record<string, unknown>,
  key: string,
): unknown =>
  key
    .split('.')
    .reduce<unknown>((current, segment) => ownValue(current, segment), values);

export const setPath = (
  values: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> => assignPath(values, key.split('.'), value);

export const isVisible = (
  field: FormField,
  values: Record<string, unknown>,
): boolean => {
  if (field.visibleIf == null) {
    return true;
  }
  const { field: source, equalsValue } = field.visibleIf;
  return deepEqual(getPath(values, source) ?? null, equalsValue ?? null);
};

export interface Visibility {
  values: Record<string, unknown>;
  field: (field: FormField) => boolean;
  key: (key: string) => boolean;
}

export const visibilityOf = (
  fields: FormField[],
  values: Record<string, unknown>,
  outer?: Visibility,
): Visibility => {
  const byKey = new Map(fields.map(item => [item.key, item]));
  const known = new Map<string, boolean>();
  const resolve = (field: FormField, trail: string[]): boolean => {
    const cached = known.get(field.key);
    if (cached !== undefined) {
      return cached;
    }
    if (field.visibleIf == null) {
      return true;
    }
    if (trail.includes(field.key)) {
      return false;
    }
    const source = field.visibleIf.field;
    const controller = byKey.get(source);
    let visible = isVisible(field, values);
    if (visible && controller != null) {
      visible = resolve(controller, [...trail, field.key]);
    } else if (visible && outer != null) {
      visible = outer.key(source);
    }
    known.set(field.key, visible);
    return visible;
  };
  return {
    values,
    field: (field: FormField): boolean => resolve(field, []),
    key: (key: string): boolean => {
      const field = byKey.get(key);
      if (field != null) {
        return resolve(field, []);
      }
      return outer != null ? outer.key(key) : true;
    },
  };
};

export const itemVisibility = (
  itemFields: FormField[],
  item: Record<string, unknown>,
  outer: Visibility,
): Visibility => visibilityOf(itemFields, { ...outer.values, ...item }, outer);

export const validateField = (
  field: FormField,
  value: unknown,
): string | undefined => {
  if (field.type === 'note') {
    return undefined;
  }
  if (isEmpty(value)) {
    return field.required ? REQUIRED : undefined;
  }
  return checkType(field, value) ?? checkText(field, value);
};

export const validateStep = (
  step: FormStep,
  values: Record<string, unknown>,
  visibility: Visibility = visibilityOf(step.fields, values),
): FieldErrors =>
  step.fields
    .filter(field => field.readOnly !== true && visibility.field(field))
    .reduce<FieldErrors>((errors, field) => {
      const message = validateField(field, getPath(values, field.key));
      return message === undefined
        ? errors
        : { ...errors, [field.key]: message };
    }, {});

export const changedKeys = (
  original: Record<string, unknown>,
  current: Record<string, unknown>,
  fields: FormField[],
): string[] =>
  fields
    .map(field => field.key)
    .filter(key => !deepEqual(getPath(original, key), getPath(current, key)));

export const flattenFields = (def: FormDefinition): FormField[] =>
  def.steps.flatMap(step => step.fields);
